/**
 * WorkspaceWebhookConfig - Componente de configuração de webhooks por workspace
 * 
 * Permite configurar MÚLTIPLOS webhooks para receber dados de sistemas externos
 * e criar negócios automaticamente no CRM.
 * 
 * @author Sistema
 * @version 2.0.0 - Suporte a múltiplos webhooks
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Webhook,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  Shield,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Activity,
  Eye,
  EyeOff,
  Info,
  Trash2,
  Settings2,
  Play,
  Send,
  Loader2,
  Plus,
  ArrowLeft,
  List,
  ChevronDown,
  ChevronUp,
  HelpCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaceWebhooks, WorkspaceWebhook, WebhookLog, PaginatedWebhookLogsResult, WebhookVolumeData } from '@/hooks/useWorkspaceWebhooks';
import { WebhookVolumeChart } from '@/components/crm/WebhookVolumeChart';
import { WebhookTutorialModal } from '@/components/crm/WebhookTutorialModal';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { LEAD_SOURCES, getLeadSourceIcon } from '@/lib/lead-sources';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
// WebhookCard removido - agora usa tabela para melhor escalabilidade

// Logger para debug condicional
const logger = createLogger('WorkspaceWebhookConfig');

/**
 * Interface do workspace (simplificada)
 */
interface WorkspaceInfo {
  id: string;
  name: string;
  color: string;
}

/**
 * Props do componente
 */
interface WorkspaceWebhookConfigProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: WorkspaceInfo | null;
}

/**
 * Interface para pipeline
 */
interface Pipeline {
  id: string;
  name: string;
  is_default: boolean;
}

/**
 * Interface para fase
 */
interface Phase {
  id: string;
  name: string;
  color: string | null;
  is_won: boolean;
  is_lost: boolean;
}

/**
 * Interface para vendedor
 */
interface Seller {
  id: string;
  name: string;
}

/**
 * Modos de visualização do componente
 */
type ViewMode = 'list' | 'create' | 'edit';

/**
 * Estado do formulário de configuração
 */
interface WebhookFormState {
  name: string;
  is_active: boolean;
  default_pipeline_id: string;
  default_phase_id: string;
  default_source: string;
  default_seller_id: string;
  check_duplicate: boolean;
  duplicate_scope: 'pipeline' | 'workspace';
  duplicate_window_value: number;
  duplicate_window_unit: string;
  // Vocabulário canônico aceito pela RPC create_crm_deal_with_check.
  // Valores legados ('skip' / 'update') são migrados ao carregar o webhook.
  duplicate_behavior:
    | 'skip_if_open'
    | 'update_phase'
    | 'create_if_all_closed'
    | 'always_create';
  require_auth: boolean;
  auth_header_name: string;
  field_mapping: {
    contact_name: string;
    contact_phone: string;
    contact_email: string;
    deal_title: string;
    deal_description: string;
    deal_tags: string; // v1.4.0: Campo para tags dinâmicas
  };
}

/**
 * Valores iniciais do formulário
 */
const initialFormState: WebhookFormState = {
  name: 'Webhook Principal',
  is_active: true,
  default_pipeline_id: '',
  default_phase_id: '',
  default_source: '',
  default_seller_id: '',
  check_duplicate: true,
  duplicate_scope: 'pipeline',
  duplicate_window_value: 7,
  duplicate_window_unit: 'days',
  duplicate_behavior: 'skip_if_open',
  require_auth: false,
  auth_header_name: '',
  field_mapping: {
    contact_name: 'nome',
    contact_phone: 'telefone',
    contact_email: 'email',
    deal_title: 'titulo',
    deal_description: 'descricao',
    deal_tags: 'tags' // v1.4.0: Campo para tags dinâmicas
  }
};

/**
 * Componente principal de configuração de webhooks
 */
export function WorkspaceWebhookConfig({ 
  open, 
  onOpenChange, 
  workspace 
}: WorkspaceWebhookConfigProps) {
  const { t } = useTranslation('crm');
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  // Hook de webhooks
  const { 
    webhooks, 
    isLoading: isLoadingWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    regenerateToken,
    fetchWebhookLogs,
    fetchWebhookLogsPaginated,
    fetchWebhookVolume,
    getWebhookUrl,
    isCreating,
    isUpdating,
    isDeleting,
    isRegeneratingToken
  } = useWorkspaceWebhooks(workspace?.id);

  // ==================== NOVOS ESTADOS PARA MÚLTIPLOS WEBHOOKS ====================
  
  // Modo de visualização: lista, criar ou editar
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  
  // Webhook selecionado para edição
  const [selectedWebhook, setSelectedWebhook] = useState<WorkspaceWebhook | null>(null);
  
  // Webhook selecionado para exclusão (confirmação)
  const [webhookToDelete, setWebhookToDelete] = useState<WorkspaceWebhook | null>(null);
  
  // Webhook selecionado para teste
  const [webhookToTest, setWebhookToTest] = useState<WorkspaceWebhook | null>(null);

  // ==================== ESTADOS EXISTENTES ====================
  
  const [activeTab, setActiveTab] = useState('config');
  const [formState, setFormState] = useState<WebhookFormState>(initialFormState);
  const [isCopied, setIsCopied] = useState(false);
  const [showAuthValue, setShowAuthValue] = useState(false);
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsFilter, setLogsFilter] = useState<'all' | 'success' | 'error' | 'duplicate'>('all');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  
  // Estados de paginação para logs (server-side)
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(10);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
  
  // Estados para teste integrado
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [testPayload, setTestPayload] = useState('');
  const [testResult, setTestResult] = useState<{
    success: boolean;
    status: number;
    data: unknown;
    time: number;
  } | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Estados para gráfico de volume
  const [volumeData, setVolumeData] = useState<WebhookVolumeData[]>([]);
  const [isLoadingVolume, setIsLoadingVolume] = useState(false);

  // Estado para modal de tutorial
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);

  // ==================== ESTADOS DE PAGINAÇÃO DA LISTA ====================
  
  // Índice da página atual (0-indexed)
  const [listPageIndex, setListPageIndex] = useState(0);
  
  // Quantidade de itens por página
  const listPageSize = 10;

  // ==================== QUERIES ====================

  // Query para buscar pipelines do workspace
  const { data: pipelines = [] } = useQuery({
    queryKey: ['workspace-pipelines-select', workspace?.id, currentTenant?.id],
    queryFn: async () => {
      if (!workspace?.id || !currentTenant?.id) return [];
      
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select('id, name, is_default')
        .eq('workspace_id', workspace.id)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data as Pipeline[];
    },
    enabled: !!workspace?.id && !!currentTenant?.id && open
  });

  // Query para buscar fases do pipeline selecionado
  const { data: phases = [] } = useQuery({
    queryKey: ['pipeline-phases-select', formState.default_pipeline_id],
    queryFn: async () => {
      if (!formState.default_pipeline_id) return [];
      
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color, is_won, is_lost')
        .eq('pipeline_id', formState.default_pipeline_id)
        .order('sort_order');
      
      if (error) throw error;
      return data as Phase[];
    },
    enabled: !!formState.default_pipeline_id && open
  });

  // Vendedores
  const [sellers, setSellers] = useState<Seller[]>([]);
  useEffect(() => {
    const fetchSellers = async () => {
      if (!currentTenant?.id || !open) return;
      
      const { data, error } = await (supabase as any)
        .from('crm_sellers')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');
      
      if (!error && data) {
        setSellers(data as Seller[]);
      }
    };
    fetchSellers();
  }, [currentTenant?.id, open]);

  // ==================== MEMOIZED DATA ====================
  
  // Mapa de pipelines por ID para lookup rápido
  const pipelinesMap = useMemo(() => {
    const map = new Map<string, Pipeline>();
    pipelines.forEach(p => map.set(p.id, p));
    return map;
  }, [pipelines]);

  // Total de páginas para paginação da lista
  const listTotalPages = useMemo(() => {
    return Math.ceil((webhooks?.length || 0) / listPageSize);
  }, [webhooks?.length, listPageSize]);

  // Webhooks da página atual (paginados)
  const pagedWebhooks = useMemo(() => {
    if (!webhooks) return [];
    const start = listPageIndex * listPageSize;
    const end = start + listPageSize;
    return webhooks.slice(start, end);
  }, [webhooks, listPageIndex, listPageSize]);

  // Mapa de fases por ID para lookup rápido (precisa buscar todas as fases)
  const [allPhasesMap, setAllPhasesMap] = useState<Map<string, Phase>>(new Map());
  
  useEffect(() => {
    const fetchAllPhases = async () => {
      if (!currentTenant?.id || !open) return;
      
      const pipelineIds = webhooks?.map(w => w.default_pipeline_id).filter(Boolean) || [];
      if (pipelineIds.length === 0) return;
      
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color, is_won, is_lost')
        .in('pipeline_id', pipelineIds);
      
      if (!error && data) {
        const map = new Map<string, Phase>();
        data.forEach(p => map.set(p.id, p));
        setAllPhasesMap(map);
      }
    };
    fetchAllPhases();
  }, [webhooks, currentTenant?.id, open]);

  // ==================== EFFECTS ====================

  // Quando muda para modo editar, preenche o formulário com dados do webhook
  useEffect(() => {
    if (viewMode === 'edit' && selectedWebhook) {
      const fieldMapping = selectedWebhook.field_mapping as Record<string, string> || {};
      setFormState({
        name: selectedWebhook.name,
        is_active: selectedWebhook.is_active,
        default_pipeline_id: selectedWebhook.default_pipeline_id || '',
        default_phase_id: selectedWebhook.default_phase_id || '',
        default_source: selectedWebhook.default_source || '',
        default_seller_id: selectedWebhook.default_seller_id || '',
        check_duplicate: selectedWebhook.check_duplicate,
        duplicate_scope: (selectedWebhook.duplicate_scope as 'pipeline' | 'workspace') || 'pipeline',
        duplicate_window_value: selectedWebhook.duplicate_window_value || 7,
        duplicate_window_unit: selectedWebhook.duplicate_window_unit || 'days',
        duplicate_behavior: ((): WebhookFormState['duplicate_behavior'] => {
          // Migra valores legados ('skip'/'update') ao carregar do backend
          const raw = (selectedWebhook.duplicate_behavior || '').toLowerCase();
          if (raw === 'skip') return 'skip_if_open';
          if (raw === 'update') return 'update_phase';
          if (
            raw === 'skip_if_open' ||
            raw === 'update_phase' ||
            raw === 'create_if_all_closed' ||
            raw === 'always_create'
          ) {
            return raw;
          }
          return 'skip_if_open';
        })(),
        require_auth: selectedWebhook.require_auth,
        auth_header_name: selectedWebhook.auth_header_name || '',
        field_mapping: {
          contact_name: fieldMapping.contact_name || 'nome',
          contact_phone: fieldMapping.contact_phone || 'telefone',
          contact_email: fieldMapping.contact_email || 'email',
          deal_title: fieldMapping.deal_title || 'titulo',
          deal_description: fieldMapping.deal_description || 'descricao',
          deal_tags: fieldMapping.deal_tags || 'tags' // v1.4.0: Campo para tags dinâmicas
        }
      });
      setActiveTab('config');
    }
  }, [viewMode, selectedWebhook]);

  // Quando muda para modo criar, reseta formulário
  useEffect(() => {
    if (viewMode === 'create') {
      const defaultPipeline = pipelines.find(p => p.is_default) || pipelines[0];
      setFormState({
        ...initialFormState,
        name: `Webhook ${(webhooks?.length || 0) + 1}`,
        default_pipeline_id: defaultPipeline?.id || ''
      });
      setSelectedWebhook(null);
      setActiveTab('config');
    }
  }, [viewMode, pipelines, webhooks?.length]);

  // Reset ao abrir o dialog
  useEffect(() => {
    if (open) {
      setViewMode('list');
      setSelectedWebhook(null);
      setFormState(initialFormState);
      setListPageIndex(0); // Reset da paginação da lista
      // Reset da paginação de logs
      setLogsPage(1);
      setLogsTotalCount(0);
      setLogs([]);
    }
  }, [open]);

  // Carrega logs quando muda para aba de logs ou muda paginação
  useEffect(() => {
    if (activeTab === 'logs' && selectedWebhook) {
      loadLogs();
    }
  }, [activeTab, selectedWebhook?.id, logsFilter, logsPage, logsPageSize]);

  // Carrega dados de volume quando muda para aba de estatísticas
  useEffect(() => {
    if (activeTab === 'stats' && selectedWebhook) {
      loadVolumeData();
    }
  }, [activeTab, selectedWebhook?.id]);

  // ==================== HANDLERS ====================

  /**
   * Carrega logs do webhook selecionado com paginação server-side
   */
  const loadLogs = async () => {
    if (!selectedWebhook) return;
    
    setIsLoadingLogs(true);
    try {
      const result = await fetchWebhookLogsPaginated(
        selectedWebhook.id,
        logsPage,
        logsPageSize,
        logsFilter === 'all' ? undefined : logsFilter
      );
      setLogs(result.logs);
      setLogsTotalCount(result.totalCount);
    } catch (error) {
      logger.error('Erro ao carregar logs paginados', error);
      setLogs([]);
      setLogsTotalCount(0);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  /**
   * Carrega dados de volume por hora para o gráfico
   */
  const loadVolumeData = async () => {
    if (!selectedWebhook) return;
    
    setIsLoadingVolume(true);
    try {
      const data = await fetchWebhookVolume(selectedWebhook.id, 24);
      setVolumeData(data);
    } catch (error) {
      logger.error('Erro ao carregar volume', error);
      setVolumeData([]);
    } finally {
      setIsLoadingVolume(false);
    }
  };

  /**
   * Handler para mudança de filtro de logs - reseta para página 1
   */
  const handleLogsFilterChange = (value: string) => {
    setLogsFilter(value as 'all' | 'success' | 'error' | 'duplicate');
    setLogsPage(1);
  };

  /**
   * Copia URL do webhook para clipboard
   */
  const handleCopyUrl = async () => {
    if (!selectedWebhook) return;
    
    const url = getWebhookUrl(selectedWebhook.webhook_token);
    await navigator.clipboard.writeText(url);
    setIsCopied(true);
    toast.success('URL copiada para a área de transferência');
    setTimeout(() => setIsCopied(false), 2000);
  };

  /**
   * Copia URL de um webhook específico pelo token (para uso na tabela)
   * @param token - Token do webhook
   */
  const handleCopyUrlForToken = async (token: string) => {
    const url = getWebhookUrl(token);
    await navigator.clipboard.writeText(url);
    toast.success('URL copiada para a área de transferência');
  };

  /**
   * Formata a URL do webhook para preview truncado na tabela
   * Mostra início e final para identificação sem ocupar muito espaço
   * @param url - URL completa do webhook
   * @returns URL formatada truncada
   */
  const formatWebhookEndpointPreview = (url: string): string => {
    // Extrai apenas o token da URL para exibir truncado
    const tokenMatch = url.match(/token=([a-f0-9-]+)/i);
    if (tokenMatch && tokenMatch[1]) {
      const token = tokenMatch[1];
      // Mostra primeiros 8 e últimos 4 caracteres do token
      if (token.length > 16) {
        return `...token=${token.slice(0, 8)}...${token.slice(-4)}`;
      }
      return `...token=${token}`;
    }
    // Fallback: trunca a URL inteira
    if (url.length > 40) {
      return `${url.slice(0, 20)}...${url.slice(-10)}`;
    }
    return url;
  };

  /**
   * Salva configurações do webhook (criar ou atualizar)
   */
  const handleSave = async () => {
    // Validações
    if (!formState.default_pipeline_id) {
      toast.error('Selecione um pipeline padrão');
      return;
    }
    if (!formState.default_phase_id) {
      toast.error('Selecione uma fase padrão');
      return;
    }
    if (formState.require_auth && !formState.auth_header_name.trim()) {
      toast.error('Informe o valor do header de autenticação');
      return;
    }

    const webhookData = {
      name: formState.name,
      is_active: formState.is_active,
      default_pipeline_id: formState.default_pipeline_id,
      default_phase_id: formState.default_phase_id,
      default_source: formState.default_source || undefined,
      default_seller_id: formState.default_seller_id || undefined,
      check_duplicate: formState.check_duplicate,
      duplicate_scope: formState.duplicate_scope,
      duplicate_window_value: formState.duplicate_window_value,
      duplicate_window_unit: formState.duplicate_window_unit,
      duplicate_behavior: formState.duplicate_behavior,
      require_auth: formState.require_auth,
      auth_header_value: formState.require_auth ? formState.auth_header_name : undefined,
      field_mapping: formState.field_mapping
    };

    try {
      if (viewMode === 'edit' && selectedWebhook) {
        // Atualiza webhook existente
        await updateWebhook({ webhook_id: selectedWebhook.id, ...webhookData });
        toast.success('Webhook atualizado com sucesso!');
      } else {
        // Cria novo webhook
        if (!workspace?.id) {
          toast.error('Workspace não identificado.');
          return;
        }
        await createWebhook({
          workspace_id: workspace.id,
          ...webhookData
        } as any);
        toast.success('Webhook criado com sucesso!');
      }
      
      // Retorna ao modo lista após salvar
      setViewMode('list');
      setSelectedWebhook(null);
      setFormState(initialFormState);
    } catch (error) {
      logger.error('Erro ao salvar webhook', error);
    }
  };

  /**
   * Handler para editar webhook
   */
  const handleEditWebhook = (webhook: WorkspaceWebhook) => {
    setSelectedWebhook(webhook);
    setViewMode('edit');
  };

  /**
   * Handler para solicitar exclusão de webhook
   */
  const handleRequestDelete = (webhook: WorkspaceWebhook) => {
    setWebhookToDelete(webhook);
    setIsDeleteDialogOpen(true);
  };

  /**
   * Confirma exclusão do webhook
   */
  const handleConfirmDelete = async () => {
    if (!webhookToDelete) return;
    await deleteWebhook(webhookToDelete.id);
    setIsDeleteDialogOpen(false);
    setWebhookToDelete(null);
    toast.success('Webhook excluído com sucesso!');
  };

  /**
   * Handler para testar webhook
   */
  const handleTestWebhook = (webhook: WorkspaceWebhook) => {
    setWebhookToTest(webhook);
    setTestPayload(getDefaultTestPayload());
    setTestResult(null);
    setIsTestDialogOpen(true);
  };

  /**
   * Confirma regeneração do token
   */
  const handleConfirmRegenerate = async () => {
    if (!selectedWebhook) return;
    await regenerateToken(selectedWebhook.id);
    setIsRegenerateDialogOpen(false);
  };

  /**
   * Gera payload padrão para teste
   */
  const getDefaultTestPayload = () => JSON.stringify({
    nome: "Lead de Teste",
    telefone: "5511999999999",
    email: "teste@exemplo.com",
    titulo: "Negócio de Teste via Webhook",
    descricao: "Criado automaticamente para validar a configuração do webhook",
    tags: ["origem-teste", "webhook-validacao"]  // Tags dinâmicas de exemplo
  }, null, 2);

  /**
   * Envia requisição de teste para o webhook
   */
  const handleSendTest = async () => {
    const webhook = webhookToTest || selectedWebhook;
    if (!webhook) return;
    
    setIsTesting(true);
    setTestResult(null);
    
    const startTime = Date.now();
    
    try {
      // Valida JSON antes de enviar
      let parsedPayload;
      try {
        parsedPayload = JSON.parse(testPayload);
      } catch {
        toast.error('JSON inválido. Verifique a sintaxe do payload.');
        setIsTesting(false);
        return;
      }
      
      const url = getWebhookUrl(webhook.webhook_token);
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Adiciona header de autenticação se configurado
      if (webhook.require_auth && webhook.auth_header_value) {
        const headerName = webhook.auth_header_name || 'X-Webhook-Secret';
        headers[headerName] = webhook.auth_header_value;
      }
      
      logger.debug('Enviando teste:', { url, payload: parsedPayload });
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: testPayload
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        data = { message: 'Resposta não é JSON válido' };
      }
      
      const elapsedTime = Date.now() - startTime;
      
      setTestResult({
        success: response.ok,
        status: response.status,
        data,
        time: elapsedTime
      });
      
      if (response.ok) {
        toast.success(`Teste executado com sucesso! (${elapsedTime}ms)`);
      } else {
        toast.error(`Erro no teste: ${data.error || `HTTP ${response.status}`}`);
      }
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      logger.error('Erro ao enviar teste:', error);
      
      setTestResult({
        success: false,
        status: 0,
        data: { error: (error as Error).message },
        time: elapsedTime
      });
      
      toast.error('Erro ao enviar requisição de teste');
    } finally {
      setIsTesting(false);
    }
  };

  // ==================== HELPERS ====================

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
  };

  const formatRelativeTime = (dateStr: string) => {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: ptBR });
  };

  const getLogStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30">Sucesso</Badge>;
      case 'error':
        return <Badge variant="destructive">Erro</Badge>;
      case 'duplicate':
        return <Badge className="bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30">Duplicado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const stats = selectedWebhook ? {
    total: selectedWebhook.total_requests,
    success: selectedWebhook.successful_deals,
    error: selectedWebhook.failed_requests,
    successRate: selectedWebhook.total_requests > 0 
      ? Math.round((selectedWebhook.successful_deals / selectedWebhook.total_requests) * 100) 
      : 0,
    lastRequest: selectedWebhook.last_request_at
  } : null;

  // Se não tem workspace, não renderiza
  if (!workspace) return null;

  // ==================== RENDER ====================

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex flex-row items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" style={{ color: workspace.color }} />
                {viewMode === 'list' && 'Webhooks de Integração'}
                {viewMode === 'create' && 'Novo Webhook'}
                {viewMode === 'edit' && `Editar: ${selectedWebhook?.name || 'Webhook'}`}
              </DialogTitle>
              <DialogDescription>
                {viewMode === 'list' && (
                  <>Gerencie os webhooks do workspace <strong>{workspace.name}</strong> para receber dados de sistemas externos.</>
                )}
                {viewMode === 'create' && (
                  <>Configure um novo endpoint para criar negócios automaticamente no workspace <strong>{workspace.name}</strong>.</>
                )}
                {viewMode === 'edit' && (
                  <>Edite as configurações do webhook.</>
                )}
              </DialogDescription>
            </div>
            
            {/* Botão Tutorial - visível apenas nos modos criar/editar */}
            {(viewMode === 'create' || viewMode === 'edit') && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsTutorialOpen(true)}
                      className="shrink-0"
                    >
                      <HelpCircle className="h-4 w-4 mr-1.5" />
                      Tutorial?
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Guia completo de integração
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </DialogHeader>

          {/* ==================== MODO LISTA (Tabela com Paginação) ==================== */}
          {viewMode === 'list' && (
            <div className="flex-1 flex flex-col min-h-0 py-4">
              {/* Header: Botão Adicionar */}
              <div className="flex justify-end mb-4">
                <Button onClick={() => setViewMode('create')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Webhook
                </Button>
              </div>

              {/* Conteúdo: Tabela ou Estados Vazios */}
              {isLoadingWebhooks ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : !webhooks || webhooks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Webhook className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">Nenhum webhook configurado</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Crie um webhook para receber dados de sistemas externos e criar negócios automaticamente.
                  </p>
                  <Button onClick={() => setViewMode('create')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Webhook
                  </Button>
                </div>
              ) : (
                <>
                  {/* Container da Tabela com scroll */}
                  <div className="border rounded-md flex-1 min-h-0">
                    <div className="max-h-[50vh] overflow-auto">
                      <Table className="min-w-[700px]">
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead className="w-[180px]">Nome</TableHead>
                            <TableHead className="w-[80px]">Status</TableHead>
                            <TableHead className="w-[200px]">Destino</TableHead>
                            <TableHead className="w-[80px] text-center">Requisições</TableHead>
                            <TableHead className="w-[80px] text-center">Sucesso</TableHead>
                            <TableHead>Endpoint</TableHead>
                            <TableHead className="w-[100px] text-right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pagedWebhooks.map(webhook => {
                            const pipeline = pipelinesMap.get(webhook.default_pipeline_id || '');
                            const phase = allPhasesMap.get(webhook.default_phase_id || '');
                            const successRate = webhook.total_requests > 0 
                              ? Math.round((webhook.successful_deals / webhook.total_requests) * 100) 
                              : 0;
                            const webhookUrl = getWebhookUrl(webhook.webhook_token);
                            
                            return (
                              <TableRow 
                                key={webhook.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => handleEditWebhook(webhook)}
                              >
                                {/* Nome */}
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <Webhook className="h-4 w-4 text-primary flex-shrink-0" />
                                    <span className="truncate">{webhook.name}</span>
                                  </div>
                                </TableCell>
                                
                                {/* Status */}
                                <TableCell>
                                  <Badge variant={webhook.is_active ? 'default' : 'secondary'}>
                                    {webhook.is_active ? 'Ativo' : 'Inativo'}
                                  </Badge>
                                </TableCell>
                                
                                {/* Destino: Pipeline → Fase */}
                                <TableCell>
                                  <div className="text-sm">
                                    {pipeline ? (
                                      <>
                                        <span className="font-medium">{pipeline.name}</span>
                                        {phase && (
                                          <span className="text-muted-foreground"> → {phase.name}</span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-muted-foreground italic">Não configurado</span>
                                    )}
                                  </div>
                                </TableCell>
                                
                                {/* Requisições */}
                                <TableCell className="text-center">
                                  {webhook.total_requests}
                                </TableCell>
                                
                                {/* Taxa de Sucesso */}
                                <TableCell className="text-center">
                                  <span className={cn(
                                    successRate >= 80 && 'text-green-600',
                                    successRate >= 50 && successRate < 80 && 'text-yellow-600',
                                    successRate < 50 && webhook.total_requests > 0 && 'text-red-600'
                                  )}>
                                    {webhook.total_requests > 0 ? `${successRate}%` : '-'}
                                  </span>
                                </TableCell>
                                
                                {/* Endpoint Truncado + Copiar */}
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs text-muted-foreground truncate max-w-[180px]">
                                      {formatWebhookEndpointPreview(webhookUrl)}
                                    </code>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-6 w-6 flex-shrink-0"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCopyUrlForToken(webhook.webhook_token);
                                            }}
                                          >
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Copiar URL completa</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </TableCell>
                                
                                {/* Ações */}
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-7 w-7"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleTestWebhook(webhook);
                                            }}
                                          >
                                            <Play className="h-3.5 w-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Testar</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-7 w-7 text-destructive hover:text-destructive"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRequestDelete(webhook);
                                            }}
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Excluir</TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Paginação */}
                  {webhooks.length > listPageSize && (
                    <div className="flex items-center justify-between pt-4 border-t mt-4">
                      <span className="text-sm text-muted-foreground">
                        Mostrando {listPageIndex * listPageSize + 1}-{Math.min((listPageIndex + 1) * listPageSize, webhooks.length)} de {webhooks.length}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setListPageIndex(prev => Math.max(0, prev - 1))}
                          disabled={listPageIndex === 0}
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setListPageIndex(prev => Math.min(listTotalPages - 1, prev + 1))}
                          disabled={listPageIndex >= listTotalPages - 1}
                        >
                          Próximo
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Footer do modo lista (quando não há paginação ou lista vazia) */}
              {(!webhooks || webhooks.length <= listPageSize) && (
                <div className="flex justify-end pt-4 border-t mt-4">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Fechar
                  </Button>
                </div>
              )}
              
              {/* Footer quando há paginação */}
              {webhooks && webhooks.length > listPageSize && (
                <div className="flex justify-end pt-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Fechar
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ==================== MODO CRIAR/EDITAR ==================== */}
          {(viewMode === 'create' || viewMode === 'edit') && (
            <>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="config" className="flex items-center gap-1.5">
                    <Settings2 className="h-4 w-4" />
                    Configuração
                  </TabsTrigger>
                  <TabsTrigger 
                    value="logs" 
                    className="flex items-center gap-1.5"
                    disabled={viewMode === 'create'}
                  >
                    <Activity className="h-4 w-4" />
                    Logs
                  </TabsTrigger>
                  <TabsTrigger 
                    value="stats" 
                    className="flex items-center gap-1.5"
                    disabled={viewMode === 'create'}
                  >
                    <Activity className="h-4 w-4" />
                    Estatísticas
                  </TabsTrigger>
                </TabsList>

                {/* Tab de Configuração */}
                <TabsContent value="config" className="flex-1 overflow-hidden mt-4">
                  <ScrollArea className="h-[calc(60vh-100px)] pr-4">
                    <div className="space-y-6">
                      {/* URL do Endpoint (apenas no modo editar) */}
                      {viewMode === 'edit' && selectedWebhook && (
                        <div className="space-y-2">
                          <Label>URL do Endpoint</Label>
                          <div className="flex gap-2">
                            <Input 
                              value={getWebhookUrl(selectedWebhook.webhook_token)}
                              readOnly
                              className="font-mono text-sm bg-muted"
                            />
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={handleCopyUrl}
                                  >
                                    {isCopied ? (
                                      <Check className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <Copy className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Copiar URL</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={() => setIsRegenerateDialogOpen(true)}
                                    disabled={isRegeneratingToken}
                                  >
                                    <RefreshCw className={cn(
                                      "h-4 w-4",
                                      isRegeneratingToken && "animate-spin"
                                    )} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Regenerar Token</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                              Envie requisições POST para esta URL com os dados do negócio.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              onClick={() => handleTestWebhook(selectedWebhook)}
                            >
                              <Play className="h-4 w-4" />
                              Testar Webhook
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Nome do Webhook */}
                      <div className="space-y-2">
                        <Label>Nome do Webhook *</Label>
                        <Input
                          value={formState.name}
                          onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Ex: Webhook Facebook Ads"
                        />
                      </div>

                      {/* Status ativo/inativo */}
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center gap-3">
                          {formState.is_active ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-muted-foreground" />
                          )}
                          <div>
                            <p className="font-medium">Webhook {formState.is_active ? 'Ativo' : 'Inativo'}</p>
                            <p className="text-sm text-muted-foreground">
                              {formState.is_active 
                                ? 'Recebendo requisições normalmente' 
                                : 'Requisições serão rejeitadas'}
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={formState.is_active}
                          onCheckedChange={(checked) => setFormState(prev => ({ ...prev, is_active: checked }))}
                        />
                      </div>

                      <Separator />

                      {/* Destino do Negócio */}
                      <div className="space-y-4">
                        <h3 className="font-semibold flex items-center gap-2">
                          <Webhook className="h-4 w-4" />
                          Destino do Negócio
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-4">
                          {/* Pipeline */}
                          <div className="space-y-2">
                            <Label>Pipeline *</Label>
                            <Select
                              value={formState.default_pipeline_id}
                              onValueChange={(value) => {
                                setFormState(prev => ({ 
                                  ...prev, 
                                  default_pipeline_id: value,
                                  default_phase_id: '' // Limpa fase ao mudar pipeline
                                }));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione..." />
                              </SelectTrigger>
                              <SelectContent>
                                {pipelines.map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} {p.is_default && '(Padrão)'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Fase */}
                          <div className="space-y-2">
                            <Label>Fase Inicial *</Label>
                            <Select
                              value={formState.default_phase_id}
                              onValueChange={(value) => setFormState(prev => ({ ...prev, default_phase_id: value }))}
                              disabled={!formState.default_pipeline_id}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione..." />
                              </SelectTrigger>
                              <SelectContent>
                                {phases.filter(p => !p.is_won && !p.is_lost).map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: p.color || '#94A3B8' }}
                                      />
                                      {p.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Origem */}
                          <div className="space-y-2">
                            <Label>Origem</Label>
                            <Select
                              value={formState.default_source || '__none__'}
                              onValueChange={(value) => setFormState(prev => ({ 
                                ...prev, 
                                default_source: value === '__none__' ? '' : value 
                              }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione (opcional)..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Nenhuma</SelectItem>
                                {LEAD_SOURCES.map(source => {
                                  const IconComponent = getLeadSourceIcon(source.id);
                                  return (
                                    <SelectItem key={source.id} value={source.id}>
                                      <div className="flex items-center gap-2">
                                        {IconComponent && <IconComponent className="h-4 w-4" />}
                                        <span>{source.label}</span>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Vendedor */}
                          <div className="space-y-2">
                            <Label>Vendedor Padrão</Label>
                            <Select
                              value={formState.default_seller_id || '__none__'}
                              onValueChange={(value) => setFormState(prev => ({ 
                                ...prev, 
                                default_seller_id: value === '__none__' ? '' : value 
                              }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione (opcional)..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Nenhum</SelectItem>
                                {sellers.map(s => (
                                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Mapeamento de Campos - Abre expandido por padrão */}
                      <Collapsible defaultOpen={true}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-muted/50">
                          <h3 className="font-semibold flex items-center gap-2">
                            <Settings2 className="h-4 w-4" />
                            Mapeamento de Campos
                          </h3>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-4 space-y-4">
                          <p className="text-sm text-muted-foreground mb-4">
                            Configure os nomes dos campos no JSON recebido. Use notação de ponto para campos aninhados (ex: <code className="bg-muted px-1 rounded">cliente.nome</code>).
                          </p>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Campo: Nome do Contato</Label>
                              <Input
                                value={formState.field_mapping.contact_name}
                                onChange={(e) => setFormState(prev => ({
                                  ...prev,
                                  field_mapping: { ...prev.field_mapping, contact_name: e.target.value }
                                }))}
                                placeholder="nome"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Campo: Telefone *</Label>
                              <Input
                                value={formState.field_mapping.contact_phone}
                                onChange={(e) => setFormState(prev => ({
                                  ...prev,
                                  field_mapping: { ...prev.field_mapping, contact_phone: e.target.value }
                                }))}
                                placeholder="telefone"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Campo: Email</Label>
                              <Input
                                value={formState.field_mapping.contact_email}
                                onChange={(e) => setFormState(prev => ({
                                  ...prev,
                                  field_mapping: { ...prev.field_mapping, contact_email: e.target.value }
                                }))}
                                placeholder="email"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Campo: Título do Negócio</Label>
                              <Input
                                value={formState.field_mapping.deal_title}
                                onChange={(e) => setFormState(prev => ({
                                  ...prev,
                                  field_mapping: { ...prev.field_mapping, deal_title: e.target.value }
                                }))}
                                placeholder="titulo"
                              />
                            </div>
                            <div className="space-y-2 col-span-2">
                              <Label>Campo: Descrição</Label>
                              <Input
                                value={formState.field_mapping.deal_description}
                                onChange={(e) => setFormState(prev => ({
                                  ...prev,
                                  field_mapping: { ...prev.field_mapping, deal_description: e.target.value }
                                }))}
                                placeholder="descricao"
                              />
                            </div>
                            
                            {/* v1.4.0: Campo para tags dinâmicas */}
                            <div className="space-y-2 col-span-2">
                              <Label className="flex items-center gap-2">
                                Campo: Tags (dinâmicas)
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs" side="right">
                                    <p className="font-medium mb-1">Tags são criadas automaticamente!</p>
                                    <p className="text-xs">Aceita array de strings ou string separada por vírgulas.</p>
                                    <p className="text-xs mt-1">Exemplo: <code className="bg-muted px-1 rounded">["criativo-1", "facebook"]</code></p>
                                    <p className="text-xs">ou <code className="bg-muted px-1 rounded">"criativo-1,facebook"</code></p>
                                  </TooltipContent>
                                </Tooltip>
                              </Label>
                              <Input
                                value={formState.field_mapping.deal_tags}
                                onChange={(e) => setFormState(prev => ({
                                  ...prev,
                                  field_mapping: { ...prev.field_mapping, deal_tags: e.target.value }
                                }))}
                                placeholder="tags"
                              />
                              <p className="text-xs text-muted-foreground">
                                Tags inexistentes são criadas automaticamente. Útil para rastrear origens (ex: criativo-1, facebook-ads).
                              </p>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <Separator />

                      {/* Duplicação - Expande automaticamente quando ativado */}
                      <Collapsible open={formState.check_duplicate}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-muted/50">
                          <h3 className="font-semibold flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Controle de Duplicação
                          </h3>
                          <Switch
                            checked={formState.check_duplicate}
                            onCheckedChange={(checked) => setFormState(prev => ({ ...prev, check_duplicate: checked }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Escopo de Verificação</Label>
                              <Select
                                value={formState.duplicate_scope}
                                onValueChange={(value: 'pipeline' | 'workspace') => 
                                  setFormState(prev => ({ ...prev, duplicate_scope: value }))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pipeline">Apenas no Pipeline</SelectItem>
                                  <SelectItem value="workspace">Todo o Workspace</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Comportamento</Label>
                              <Select
                                value={formState.duplicate_behavior}
                                onValueChange={(value: WebhookFormState['duplicate_behavior']) =>
                                  setFormState(prev => ({ ...prev, duplicate_behavior: value }))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="skip_if_open">Ignorar se houver negócio aberto</SelectItem>
                                  <SelectItem value="update_phase">Atualizar fase do negócio aberto</SelectItem>
                                  <SelectItem value="create_if_all_closed">Criar somente se todos estiverem fechados</SelectItem>
                                  <SelectItem value="always_create">Sempre criar (sem checagem)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Janela de Tempo</Label>
                              <div className="flex gap-2">
                                <Input
                                  type="number"
                                  min={1}
                                  value={formState.duplicate_window_value}
                                  onChange={(e) => setFormState(prev => ({ 
                                    ...prev, 
                                    duplicate_window_value: parseInt(e.target.value) || 7 
                                  }))}
                                  className="w-20"
                                />
                                <Select
                                  value={formState.duplicate_window_unit}
                                  onValueChange={(value) => setFormState(prev => ({ ...prev, duplicate_window_unit: value }))}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="hours">Horas</SelectItem>
                                    <SelectItem value="days">Dias</SelectItem>
                                    <SelectItem value="weeks">Semanas</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <Separator />

                      {/* Autenticação - Expande automaticamente quando ativado */}
                      <Collapsible open={formState.require_auth}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-muted/50">
                          <h3 className="font-semibold flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            Autenticação Adicional
                          </h3>
                          <Switch
                            checked={formState.require_auth}
                            onCheckedChange={(checked) => setFormState(prev => ({ ...prev, require_auth: checked }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-4 space-y-4">
                          <div className="space-y-2">
                            <Label>Valor do Header X-Webhook-Secret</Label>
                            <div className="flex gap-2">
                              <Input
                                type={showAuthValue ? 'text' : 'password'}
                                value={formState.auth_header_name}
                                onChange={(e) => setFormState(prev => ({ ...prev, auth_header_name: e.target.value }))}
                                placeholder="seu-token-secreto"
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setShowAuthValue(!showAuthValue)}
                              >
                                {showAuthValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Requisições devem incluir o header <code className="bg-muted px-1 rounded">X-Webhook-Secret</code> com este valor.
                            </p>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Tab de Logs */}
                <TabsContent value="logs" className="flex-1 overflow-hidden mt-4">
                  <div className="space-y-4 h-[calc(60vh-100px)]">
                    {/* Filtros */}
                    <div className="flex items-center gap-2">
                      <Select value={logsFilter} onValueChange={handleLogsFilterChange}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Filtrar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="success">Sucesso</SelectItem>
                          <SelectItem value="error">Erro</SelectItem>
                          <SelectItem value="duplicate">Duplicado</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={loadLogs} disabled={isLoadingLogs}>
                        <RefreshCw className={cn("h-4 w-4 mr-1", isLoadingLogs && "animate-spin")} />
                        Atualizar
                      </Button>
                    </div>

                    {/* Tabela de Logs */}
                    <ScrollArea className="h-[calc(100%-50px)] border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[180px]">Data/Hora</TableHead>
                            <TableHead className="w-[100px]">Status</TableHead>
                            <TableHead>IP</TableHead>
                            <TableHead className="w-[100px]">Tempo</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {isLoadingLogs ? (
                            Array.from({ length: 5 }).map((_, i) => (
                              <TableRow key={i}>
                                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                              </TableRow>
                            ))
                          ) : logs.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                Nenhum log encontrado
                              </TableCell>
                            </TableRow>
                          ) : (
                            logs.map(log => (
                              /* Fragment mantém estrutura semântica da tabela HTML */
                              <React.Fragment key={log.id}>
                                {/* Linha principal - clicável para expandir */}
                                <TableRow 
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                                >
                                  <TableCell className="font-mono text-xs w-[180px]">
                                    {formatDate(log.created_at)}
                                  </TableCell>
                                  <TableCell className="w-[100px]">
                                    {getLogStatusBadge(log.status)}
                                  </TableCell>
                                  <TableCell className="text-xs text-muted-foreground">
                                    {log.request_ip || '-'}
                                  </TableCell>
                                  <TableCell className="text-xs w-[100px]">
                                    {log.processing_time_ms ? `${log.processing_time_ms}ms` : '-'}
                                  </TableCell>
                                  <TableCell className="w-[50px]">
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-6 w-6"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedLogId(expandedLogId === log.id ? null : log.id);
                                      }}
                                    >
                                      {expandedLogId === log.id ? (
                                        <ChevronUp className="h-3 w-3" />
                                      ) : (
                                        <ChevronDown className="h-3 w-3" />
                                      )}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                
                                {/* Linha expansível - renderização condicional */}
                                {expandedLogId === log.id && (
                                  <TableRow>
                                    <TableCell colSpan={5} className="bg-muted/30 p-0">
                                      <div className="p-4 space-y-3">
                                        {log.error_message && (
                                          <div className="p-2 bg-destructive/10 rounded text-sm text-destructive">
                                            <strong>Erro:</strong> {log.error_message}
                                          </div>
                                        )}
                                        <div>
                                          <p className="text-xs font-medium mb-1">Payload Recebido:</p>
                                          <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-40">
                                            {JSON.stringify(log.request_payload, null, 2)}
                                          </pre>
                                        </div>
                                        {log.deal_id && (
                                          <p className="text-xs text-muted-foreground">
                                            <strong>Deal ID:</strong> {log.deal_id}
                                          </p>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>

                    {/* Paginação de Logs - Componente reutilizado */}
                    <PaginationFooter
                      totalItems={logsTotalCount}
                      currentPage={logsPage}
                      pageSize={logsPageSize}
                      onPageChange={setLogsPage}
                      onPageSizeChange={(size) => {
                        setLogsPageSize(size);
                        setLogsPage(1);
                      }}
                      pageSizeOptions={[5, 10, 20, 50]}
                      className="mt-2"
                      borderClassName=""
                    />
                  </div>
                </TabsContent>

                {/* Tab de Estatísticas */}
                <TabsContent value="stats" className="flex-1 overflow-hidden mt-4">
                  <ScrollArea className="h-[calc(60vh-100px)]">
                    <div className="space-y-6">
                      {/* Cards de métricas */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Total de Requisições */}
                        <div className="p-4 rounded-lg border bg-muted/30">
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <Activity className="h-4 w-4" />
                            <span className="text-sm">Total de Requisições</span>
                          </div>
                          <p className="text-3xl font-bold">{stats?.total || 0}</p>
                        </div>

                        {/* Deals Criados */}
                        <div className="p-4 rounded-lg border bg-muted/30">
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span className="text-sm">Deals Criados</span>
                          </div>
                          <p className="text-3xl font-bold text-green-500">{stats?.success || 0}</p>
                        </div>

                        {/* Taxa de Sucesso */}
                        <div className="p-4 rounded-lg border bg-muted/30">
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <Activity className="h-4 w-4" />
                            <span className="text-sm">Taxa de Sucesso</span>
                          </div>
                          <p className="text-3xl font-bold">{stats?.successRate || 0}%</p>
                        </div>

                        {/* Última Requisição */}
                        <div className="p-4 rounded-lg border bg-muted/30">
                          <div className="flex items-center gap-2 text-muted-foreground mb-1">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">Última Requisição</span>
                          </div>
                          <p className="text-lg font-medium">
                            {stats?.lastRequest ? formatRelativeTime(stats.lastRequest) : 'Nunca'}
                          </p>
                        </div>
                      </div>

                      {/* Gráfico de volume por hora */}
                      <WebhookVolumeChart 
                        data={volumeData} 
                        isLoading={isLoadingVolume} 
                      />
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>

              <DialogFooter className="flex justify-between items-center pt-4 border-t">
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setViewMode('list');
                      setSelectedWebhook(null);
                    }}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Voltar
                  </Button>
                  
                  {viewMode === 'edit' && selectedWebhook && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleRequestDelete(selectedWebhook)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Excluir
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    {isCreating || isUpdating ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      viewMode === 'create' ? 'Criar Webhook' : 'Salvar Alterações'
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação - Excluir */}
      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Excluir Webhook"
        description={`Tem certeza que deseja excluir o webhook "${webhookToDelete?.name || ''}"? Esta ação é irreversível e todas as integrações que utilizam este endpoint deixarão de funcionar.`}
        variant="danger"
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />

      {/* Dialog de Confirmação - Regenerar Token */}
      <ConfirmDialog
        open={isRegenerateDialogOpen}
        onOpenChange={setIsRegenerateDialogOpen}
        title="Regenerar Token"
        description="Ao regenerar o token, a URL do webhook será alterada. Todas as integrações existentes precisarão ser atualizadas com a nova URL."
        variant="warning"
        confirmText="Regenerar"
        cancelText="Cancelar"
        onConfirm={handleConfirmRegenerate}
        isLoading={isRegeneratingToken}
      />

      {/* Dialog de Teste Integrado */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Testar Webhook: {webhookToTest?.name || selectedWebhook?.name || 'Webhook'}
            </DialogTitle>
            <DialogDescription>
              Envie uma requisição de teste para validar a configuração do webhook.
              O teste criará um negócio real no sistema.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Aviso sobre teste real */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Este teste criará um negócio real. Use dados fictícios para teste.
              </p>
            </div>

            {/* Editor de Payload */}
            <div className="space-y-2">
              <Label>Payload JSON</Label>
              <Textarea
                value={testPayload}
                onChange={(e) => setTestPayload(e.target.value)}
                className="font-mono text-sm h-48 resize-none"
                placeholder='{"nome": "Teste", "telefone": "5511999999999", ...}'
              />
              <p className="text-xs text-muted-foreground">
                Os campos devem corresponder ao mapeamento configurado (nome, telefone, email, etc.).
              </p>
            </div>

            {/* Resultado do Teste */}
            {testResult && (
              <div className={cn(
                "p-4 rounded-lg border",
                testResult.success 
                  ? "bg-green-500/10 border-green-500/30" 
                  : "bg-red-500/10 border-red-500/30"
              )}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <span className="font-medium">
                      {testResult.success ? 'Sucesso!' : 'Erro'}
                    </span>
                    <Badge variant="secondary" className="font-mono">
                      HTTP {testResult.status}
                    </Badge>
                  </div>
                  <span className="text-sm text-muted-foreground font-mono">
                    {testResult.time}ms
                  </span>
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Resposta:</p>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-32 font-mono">
                    {JSON.stringify(testResult.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsTestDialogOpen(false)}>
              Fechar
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={isTesting || !testPayload.trim()}
            >
              {isTesting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Enviar Teste
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Tutorial */}
      <WebhookTutorialModal
        open={isTutorialOpen}
        onOpenChange={setIsTutorialOpen}
        webhookUrl={selectedWebhook ? getWebhookUrl(selectedWebhook.webhook_token) : ''}
        webhookToken={selectedWebhook?.webhook_token || ''}
        authHeaderName={selectedWebhook?.auth_header_name || undefined}
        authHeaderValue={selectedWebhook?.auth_header_value || undefined}
      />
    </>
  );
}
